/**
 * The global time range (IA §1.3): today, 7 days, 30 days, this month or a custom span.
 * Ranges are half-open [from, to) instants whose calendar days are in the company timezone
 * (RULES §DB-3), so "today" means today in Brussels, not in the browser's zone.
 */
import { msg } from './i18n';

export type RangeKey = 'today' | '7d' | '30d' | 'month' | 'custom';
export const RANGE_KEYS: RangeKey[] = ['today', '7d', '30d', 'month', 'custom'];
export const DEFAULT_RANGE: RangeKey = '7d';

export const RANGE_LABELS: Record<RangeKey, string> = {
  today: msg('Today'),
  '7d': msg('Last 7 days'),
  '30d': msg('Last 30 days'),
  month: msg('This month'),
  custom: msg('Custom'),
};

export interface ResolvedRange {
  key: RangeKey;
  from: Date;
  to: Date;
  /** Inclusive calendar days (YYYY-MM-DD) for display and custom inputs. */
  fromDay: string;
  toDay: string;
}

export function isRangeKey(v: unknown): v is RangeKey {
  return typeof v === 'string' && (RANGE_KEYS as string[]).includes(v);
}

export function isIsoDay(v: unknown): v is string {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
}

interface YMD {
  y: number;
  m: number;
  d: number;
}

function partsIn(instant: number, tz: string): YMD & { hh: number; mm: number; ss: number } {
  const f = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const p = Object.fromEntries(f.formatToParts(new Date(instant)).map((x) => [x.type, x.value]));
  return { y: +(p.year ?? 0), m: +(p.month ?? 0), d: +(p.day ?? 0), hh: +(p.hour ?? 0), mm: +(p.minute ?? 0), ss: +(p.second ?? 0) };
}

/** Offset of `tz` from UTC at `instant`, in milliseconds. */
function offsetMs(instant: number, tz: string): number {
  const p = partsIn(instant, tz);
  return Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mm, p.ss) - Math.floor(instant / 1000) * 1000;
}

/** The UTC instant of 00:00 on calendar day `ymd` in `tz` (DST-safe). */
export function zonedMidnight(ymd: YMD, tz: string): Date {
  const guess = Date.UTC(ymd.y, ymd.m - 1, ymd.d);
  let at = guess - offsetMs(guess, tz);
  at = guess - offsetMs(at, tz);
  return new Date(at);
}

export function dayOf(instant: Date, tz: string): YMD {
  const p = partsIn(instant.getTime(), tz);
  return { y: p.y, m: p.m, d: p.d };
}

function addDays(ymd: YMD, days: number): YMD {
  const d = new Date(Date.UTC(ymd.y, ymd.m - 1, ymd.d + days));
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate() };
}

export function isoDay(ymd: YMD): string {
  return `${ymd.y}-${String(ymd.m).padStart(2, '0')}-${String(ymd.d).padStart(2, '0')}`;
}

function parseDay(s: string): YMD {
  const [y, m, d] = s.split('-').map(Number);
  return { y: y ?? 1970, m: m ?? 1, d: d ?? 1 };
}

export function resolveRange(key: RangeKey | undefined, from: string | undefined, to: string | undefined, tz: string, now: Date = new Date()): ResolvedRange {
  const today = dayOf(now, tz);
  let first: YMD;
  let last: YMD;
  let k: RangeKey = key ?? DEFAULT_RANGE;
  switch (k) {
    case 'today':
      first = last = today;
      break;
    case '30d':
      first = addDays(today, -29);
      last = today;
      break;
    case 'month':
      first = { ...today, d: 1 };
      last = addDays({ y: today.m === 12 ? today.y + 1 : today.y, m: today.m === 12 ? 1 : today.m + 1, d: 1 }, -1);
      break;
    case 'custom':
      if (isIsoDay(from) && isIsoDay(to) && from <= to) {
        first = parseDay(from);
        last = parseDay(to);
        break;
      }
      k = DEFAULT_RANGE;
      first = addDays(today, -6);
      last = today;
      break;
    default:
      first = addDays(today, -6);
      last = today;
  }
  return {
    key: k,
    from: zonedMidnight(first, tz),
    to: zonedMidnight(addDays(last, 1), tz),
    fromDay: isoDay(first),
    toDay: isoDay(last),
  };
}
